import {
  BarElement,
  CategoryScale,
  Chart as ChartJS,
  Legend,
  LinearScale,
  LineElement,
  PointElement,
  Tooltip,
  type ChartData,
  type ChartOptions,
} from "chart.js";
import { useQuery } from "@tanstack/react-query";
import { useMemo } from "react";
import { Bar } from "react-chartjs-2";

import { ChartPanel, DashboardsPageFrame, DataTable, KpiCard } from "./dashboardKit";
import { revenueSnapshotQueryOptions } from "./revenueReactQuery";
import type { RevenueClinicRow, RevenueHcpcsMixRow, RevenueSnapshotResponse } from "./revenueTypes";

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  PointElement,
  LineElement,
  Tooltip,
  Legend,
);

const GREEN = "#2A9D8F";
const BLUE = "#0081AF";
const GOLD = "#E4B363";
const CLAY = "#C84C35";
const STONE = "#6D6A66";
const GRID = "#EDE9E4";

const usd = new Intl.NumberFormat("en-US", {
  style: "currency",
  currency: "USD",
  maximumFractionDigits: 0,
});

function fmtUsd(value: number): string {
  return usd.format(Number(value || 0));
}

function fmtInt(value: number): string {
  return Math.round(Number(value || 0)).toLocaleString();
}

function fmtPct(value: number): string {
  return `${(Number(value || 0) * 100).toFixed(1)}%`;
}

function fmtDelta(value: number | null | undefined): string | null {
  if (value === null || value === undefined) return null;
  const sign = value >= 0 ? "+" : "";
  return `${sign}${value.toFixed(1)}% vs prior`;
}

function fmtDate(value: string): string {
  return new Date(`${value}T12:00:00Z`).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

function fmtMonth(value: string): string {
  return new Date(`${value}T12:00:00Z`).toLocaleDateString("en-US", {
    month: "short",
    year: "2-digit",
  });
}

function shiftMonthStart(value: string, deltaMonths: number): string {
  const next = new Date(`${value}T12:00:00Z`);
  next.setUTCMonth(next.getUTCMonth() + deltaMonths);
  return next.toISOString().slice(0, 10);
}

function pct(numerator: number, denominator: number): number {
  return denominator > 0 ? numerator / denominator : 0;
}

function deltaDirection(
  value: number | null | undefined,
  lowerIsBetter = false,
): "up" | "down" | undefined {
  if (value === null || value === undefined) return undefined;
  const positive = lowerIsBetter ? value <= 0 : value >= 0;
  return positive ? "up" : "down";
}

function sourceLabel(source: string): string {
  if (source === "live") return "Live data";
  if (source === "mock") return "Mock data";
  return source;
}

export function RevenueDashboard() {
  const query = useQuery(revenueSnapshotQueryOptions());
  const data = query.data ?? null;

  const trendData = useMemo<ChartData<"bar">>(() => {
    const rows = data?.trend || [];
    return {
      labels: rows.map((row) => fmtMonth(row.monthStart)),
      datasets: [
        {
          type: "bar" as const,
          label: "Billed",
          data: rows.map((row) => Number(row.billedAmount || 0)),
          backgroundColor: BLUE,
          borderRadius: 4,
        },
        {
          type: "line" as const,
          label: "Paid",
          data: rows.map((row) => Number(row.paidAmount || 0)),
          borderColor: GREEN,
          backgroundColor: "transparent",
          pointBackgroundColor: GREEN,
          borderWidth: 2,
          tension: 0.25,
          pointRadius: 3,
        } as never,
      ],
    };
  }, [data]);

  const trendOptions = useMemo<ChartOptions<"bar">>(() => {
    const rows = data?.trend || [];
    return {
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: { display: true, position: "bottom" },
        tooltip: {
          callbacks: {
            afterBody(items) {
              const dataIndex = items?.[0]?.dataIndex;
              if (dataIndex === undefined) return [];
              const row = rows[dataIndex];
              if (!row) return [];
              return [
                `% Paid: ${fmtPct(pct(Number(row.paidAmount || 0), Number(row.billedAmount || 0)))}`,
              ];
            },
          },
        },
      },
      scales: {
        y: {
          grid: { color: GRID },
          ticks: {
            color: STONE,
            callback: (value) => usd.format(Number(value || 0)),
          },
        },
        x: {
          grid: { display: false },
          ticks: { color: STONE },
        },
      },
    };
  }, [data]);

  const hcpcsMonthLabels = useMemo(() => {
    if (!data?.monthStart) return null;
    return {
      current: fmtMonth(data.monthStart),
      prior1: fmtMonth(shiftMonthStart(data.monthStart, -1)),
      prior2: fmtMonth(shiftMonthStart(data.monthStart, -2)),
    };
  }, [data]);

  const queueTrendData = useMemo<ChartData<"bar">>(() => {
    const rows = data?.queueSummary || [];
    return {
      labels: rows.map((row) => fmtMonth(row.monthStart)),
      datasets: [
        {
          label: "Paid",
          data: rows.map((row) => Number(row.paidCount || 0)),
          backgroundColor: GREEN,
        },
        {
          label: "Processing",
          data: rows.map((row) => Number(row.processingCount || 0)),
          backgroundColor: BLUE,
        },
        {
          label: "Rejected",
          data: rows.map((row) => Number(row.rejectedCount || 0)),
          backgroundColor: CLAY,
        },
        {
          label: "Written Off",
          data: rows.map((row) => Number(row.writtenOffCount || 0)),
          backgroundColor: GOLD,
        },
        {
          label: "Respite Balance",
          data: rows.map((row) => Number(row.respiteBalanceCount || 0)),
          backgroundColor: "#7A7467",
        },
        {
          label: "No Claim Yet",
          data: rows.map((row) => Number(row.noClaimYetCount || 0)),
          backgroundColor: "#C9C4BC",
        },
      ],
    };
  }, [data]);

  const queueTrendOptions = useMemo<ChartOptions<"bar">>(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      plugins: { legend: { display: true, position: "bottom" } },
      scales: {
        x: { stacked: true, grid: { display: false }, ticks: { color: STONE } },
        y: {
          stacked: true,
          grid: { color: GRID },
          ticks: { color: STONE, precision: 0 },
        },
      },
    }),
    [],
  );

  return (
    <DashboardsPageFrame className="space-y-5">
      <header className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
            Finance
          </p>
          <h1 className="text-2xl font-semibold tracking-tight text-foreground">TIN Revenue</h1>
          {data ? (
            <p className="mt-1 text-sm text-muted-foreground">
              Month to date: {fmtDate(data.monthStart)} through {fmtDate(data.asOf)} · comparison
              window {fmtDate(data.priorMonthStart)} through {fmtDate(data.priorMonthEnd)}
            </p>
          ) : (
            <p className="mt-1 text-sm text-muted-foreground">
              TIN-scoped DCMP billed/paid topline, clinic payout, HCPCS mix, and claim queue trend.
            </p>
          )}
        </div>
        <div className="flex flex-col items-end gap-1 text-right">
          <span
            className={
              data?.source === "live"
                ? "rounded-md bg-emerald-50 px-2 py-1 text-xs font-medium text-emerald-800"
                : data?.source === "mock"
                  ? "rounded-md bg-amber-50 px-2 py-1 text-xs font-medium text-amber-800"
                  : "rounded-md bg-muted px-2 py-1 text-xs font-medium text-muted-foreground"
            }
          >
            {data ? sourceLabel(data.source) : query.isLoading ? "Loading data…" : "No data"}
          </span>
          {data?.generatedAt ? (
            <small className="text-xs text-muted-foreground">
              Refreshed{" "}
              {new Date(data.generatedAt).toLocaleTimeString([], {
                hour: "numeric",
                minute: "2-digit",
              })}
            </small>
          ) : null}
        </div>
      </header>

      {query.isLoading ? (
        <p className="text-sm text-muted-foreground">Loading TIN Revenue snapshot…</p>
      ) : null}

      {query.error ? (
        <div className="rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
          {query.error instanceof Error ? query.error.message : "Failed to load revenue data"}
        </div>
      ) : null}

      {data && !query.isLoading ? <RevenueBody data={data} /> : null}

      {data && !query.isLoading ? (
        <>
          <div className="grid gap-3 lg:grid-cols-[minmax(0,1.3fr)_minmax(320px,1fr)]">
            <ChartPanel title="12-Month Revenue Trend - TIN DCMP Claims">
              <p className="-mt-2 mb-3 text-xs text-muted-foreground">
                TIN DCMP billed amount by billing-entry effective month with paid overlay.
              </p>
              <div className="relative h-[300px]">
                <Bar data={trendData} options={trendOptions} />
              </div>
            </ChartPanel>

            <ChartPanel title="Top Clinics - Prior Calendar Quarter">
              <p className="-mt-2 mb-3 text-xs text-muted-foreground">
                TIN DCMP billed and paid amounts for the prior calendar quarter, excluding respite
                care and guide participant organizations.
              </p>
              <DataTable<RevenueClinicRow>
                rows={data.topClinics}
                rowKey={(row) => row.clinic}
                emptyMessage="No clinic payout rows for the prior quarter."
                columns={[
                  { key: "clinic", label: "Clinic" },
                  {
                    key: "billedAmount",
                    label: "Billed",
                    align: "right",
                    render: (row) => fmtUsd(row.billedAmount),
                  },
                  {
                    key: "paidAmount",
                    label: "Paid",
                    align: "right",
                    render: (row) => fmtUsd(row.paidAmount),
                  },
                  {
                    key: "collectionRatePct",
                    label: "Rate",
                    align: "right",
                    render: (row) => (
                      <span
                        className={
                          row.collectionRatePct >= 0.5
                            ? "font-semibold text-emerald-700"
                            : "font-semibold text-red-600"
                        }
                      >
                        {fmtPct(row.collectionRatePct)}
                      </span>
                    ),
                  },
                ]}
              />
            </ChartPanel>
          </div>

          <div className="grid gap-3 md:grid-cols-2">
            <ChartPanel title="HCPCS Mix - 3-Month Percentage">
              <p className="-mt-2 mb-3 text-xs text-muted-foreground">
                Share of billed amount by HCPCS for the current month and two prior months.
              </p>
              <DataTable<RevenueHcpcsMixRow>
                rows={data.hcpcsMix}
                rowKey={(row) => row.hcpcs}
                emptyMessage="No HCPCS mix rows for this window."
                columns={[
                  {
                    key: "hcpcs",
                    label: "HCPCS",
                    render: (row) => <span className="font-mono font-semibold">{row.hcpcs}</span>,
                  },
                  {
                    key: "currentMixPct",
                    label: hcpcsMonthLabels?.current || "Current",
                    align: "right",
                    render: (row) => fmtPct(row.currentMixPct),
                  },
                  {
                    key: "prior1MixPct",
                    label: hcpcsMonthLabels?.prior1 || "Prior 1",
                    align: "right",
                    render: (row) => fmtPct(row.prior1MixPct),
                  },
                  {
                    key: "prior2MixPct",
                    label: hcpcsMonthLabels?.prior2 || "Prior 2",
                    align: "right",
                    render: (row) => fmtPct(row.prior2MixPct),
                  },
                ]}
              />
            </ChartPanel>

            <ChartPanel title="Claim Queue Trend">
              <p className="-mt-2 mb-3 text-xs text-muted-foreground">
                Month-by-month grouped latest-claim queues for TIN DCMP entries across the trailing
                12-month window.
              </p>
              <div className="relative h-[300px]">
                <Bar data={queueTrendData} options={queueTrendOptions} />
              </div>
            </ChartPanel>
          </div>
        </>
      ) : null}
    </DashboardsPageFrame>
  );
}

function RevenueBody({ data }: { data: RevenueSnapshotResponse }) {
  return (
    <section className="grid grid-cols-2 gap-3 md:grid-cols-3 xl:grid-cols-5">
      <KpiCard
        label="Billed"
        value={fmtUsd(data.current.billedAmount)}
        footnote={`${fmtInt(data.current.entries)} entries`}
        delta={fmtDelta(data.deltas.billedAmount)}
        deltaDirection={deltaDirection(data.deltas.billedAmount)}
      />
      <KpiCard
        label="Paid"
        value={fmtUsd(data.current.paidAmount)}
        footnote={`${fmtInt(data.current.claims)} claims`}
        delta={fmtDelta(data.deltas.paidAmount)}
        deltaDirection={deltaDirection(data.deltas.paidAmount)}
      />
      <KpiCard
        label="Outstanding"
        value={fmtUsd(data.current.outstandingAmount)}
        footnote="Billed minus paid"
        delta={fmtDelta(data.deltas.outstandingAmount)}
        deltaDirection={deltaDirection(data.deltas.outstandingAmount, true)}
      />
      <KpiCard
        label="Claim Coverage"
        value={fmtPct(data.current.claimCoveragePct)}
        footnote={`${fmtInt(data.current.claimedEntries)} of ${fmtInt(data.current.entries)} entries claimed`}
        delta={fmtDelta(data.deltas.claimCoveragePct)}
        deltaDirection={deltaDirection(data.deltas.claimCoveragePct)}
      />
      <KpiCard
        label="Collection Rate"
        value={fmtPct(data.current.collectionRatePct)}
        footnote={`Prior window ${fmtPct(data.prior.collectionRatePct)}`}
        delta={fmtDelta(data.deltas.collectionRatePct)}
        deltaDirection={deltaDirection(data.deltas.collectionRatePct)}
      />
    </section>
  );
}
